import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SendHorizontal, Lock } from "lucide-react";
import { toast } from "react-toastify";
import { formatearTamano } from "../../../utilities/chatEliteAdjuntos";
import useSelectorAdjuntos from "../adjuntos/useSelectorAdjuntos";
import { BotonAdjuntar, AdjuntosPendientes, CapaSoltar } from "../adjuntos/SelectorAdjuntos";
// Mensajes de voz: el mismo grabador que Tu IA Normativa (mantener para grabar, deslizar para cancelar/bloquear)
import LAEAIGrabador from "../../../routes/pages/aaff/LAEAIGrabador";
import { grabacionSoportada } from "../../../hooks/useGrabadorAudio";
import "./ChatEliteComun.css";

const MAX_TEXTO = 4000;
const vozSoportada = grabacionSoportada();
const ERRORES_MICROFONO = { MIC_DENEGADO: "laeai.audio.micDenied", MIC_NO_DISPONIBLE: "laeai.audio.micUnavailable", SIN_VOZ: "laeai.audio.noSpeech" };
const ALTO_MAX_PX = 140;

// Borradores por ticket en memoria (al volver a la lista y entrar de nuevo no se pierde lo escrito)
const borradores = new Map();

/** Deja un texto como borrador (p. ej. "nueva-consulta" al abrir el chat desde Tu IA Normativa con la pregunta escrita). */
export const ponerBorrador = (clave, texto) => {
  if (texto) borradores.set(clave, String(texto).slice(0, MAX_TEXTO));
  else borradores.delete(clave);
};

/**
 * Caja para escribir mensajes.
 *
 * @param {Object} props
 * @param {(datos: { texto, archivos, interno }) => Promise|void} props.onEnviar
 * @param {string} [props.claveBorrador] - p.ej. "ticket-12"
 * @param {string} [props.placeholder]
 * @param {boolean} [props.deshabilitado]
 * @param {Function} [props.onEscribiendo] - (interno) => void
 * @param {boolean} [props.permitirNota] - staff: interruptor de nota interna
 * @param {boolean} [props.autoFocus]
 * @param {number} [props.filasMin]
 * @param {boolean} [props.permitirVoz] - lado AAFF: con la caja vacía, micrófono para enviar un mensaje de voz
 * @param {boolean} [props.optimista=true] - limpia al momento (el mensaje aparece como "enviando");
 *   si es false, espera al servidor y solo limpia si va bien (p.ej. crear consulta)
 * @param {React.ReactNode} [props.adjuntoFijo] - se muestra delante de los adjuntos (p.ej. la pantalla actual)
 */
const Compositor = ({
  onEnviar,
  claveBorrador,
  placeholder,
  deshabilitado = false,
  onEscribiendo,
  permitirNota = false,
  autoFocus = false,
  filasMin = 1,
  optimista = true,
  permitirVoz = false,
  adjuntoFijo = null,
}) => {
  const { t } = useTranslation("common");
  const [texto, setTexto] = useState(() => (claveBorrador && borradores.get(claveBorrador)) || "");
  const [interno, setInterno] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const textareaRef = useRef(null);
  const adjuntos = useSelectorAdjuntos();

  useEffect(() => {
    setTexto((claveBorrador && borradores.get(claveBorrador)) || "");
    setInterno(false);
    adjuntos.limpiar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveBorrador]);

  useEffect(() => {
    if (autoFocus && !deshabilitado) textareaRef.current?.focus();
  }, [autoFocus, deshabilitado, claveBorrador]);

  // Alto automatico
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    // scrollHeight no incluye el borde: sin sumarlo queda 2 px corto y sale la barra de scroll con una sola línea
    const alto = el.scrollHeight + (el.offsetHeight - el.clientHeight);
    el.style.height = `${Math.min(alto, ALTO_MAX_PX)}px`;
    el.style.overflowY = alto > ALTO_MAX_PX ? "auto" : "hidden";
  }, [texto]);

  const cambiarTexto = (valor) => {
    setTexto(valor);
    if (claveBorrador) borradores.set(claveBorrador, valor);
    if (valor.trim()) onEscribiendo?.(interno);
  };

  const puedeEnviar = !deshabilitado && !enviando && (texto.trim() || adjuntos.pendientes.length > 0);

  const enviar = async () => {
    if (!puedeEnviar) return;
    setEnviando(true);
    try {
      let archivos;
      try {
        archivos = await adjuntos.obtenerArchivos();
      } catch (error) {
        // No cabe: se avisa y no se borra nada de lo escrito ni de lo adjuntado
        const clave = error.total ? `${error.code}_TOTAL` : error.code;
        toast.error(t(`chatElite.errores.${clave}`, {
          total: formatearTamano(error.total || 0),
          defaultValue: t(`chatElite.errores.${error.code}`, { defaultValue: t("chatElite.errores.ERROR") }),
        }));
        return;
      }
      const datos = { texto: texto.trim(), archivos, interno };
      const limpiar = () => {
        setTexto("");
        if (claveBorrador) borradores.delete(claveBorrador);
        adjuntos.limpiar();
        setInterno(false);
      };
      if (optimista) {
        limpiar();
        await onEnviar(datos);
      } else {
        await onEnviar(datos);
        limpiar();
      }
    } catch (_) {
      // Optimista: el error lo muestra la burbuja (reintentar). Si no, quien llama muestra el aviso.
    } finally {
      setEnviando(false);
      textareaRef.current?.focus();
    }
  };

  // Mensaje de voz: se envía el WAV como adjunto; el back lo transcribe y la transcripción es el texto del mensaje
  const enviarVoz = async (wav) => {
    if (deshabilitado || enviando) return;
    setEnviando(true);
    try {
      await onEnviar({ texto: "", archivos: [new File([wav], "mensaje-de-voz.wav", { type: "audio/wav" })], interno: false });
    } catch (_) {
      // Optimista: el error lo muestra la burbuja (reintentar)
    } finally {
      setEnviando(false);
    }
  };
  const errorMicrofono = (codigo) => toast.error(t(ERRORES_MICROFONO[codigo] || "chatElite.errores.ERROR"));
  const mostrarMicrofono = permitirVoz && vozSoportada && !interno && !texto.trim() && !adjuntos.pendientes.length;

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      enviar();
    }
  };

  return (
    <div className={`ce-compositor ${interno ? "ce-compositor--interno" : ""}`} {...adjuntos.propsSoltar}>
      <CapaSoltar visible={adjuntos.arrastrando} />
      {permitirNota && (
        <div className="ce-compositor__modos" role="tablist">
          <button type="button" role="tab" aria-selected={!interno} className={!interno ? "activo" : ""} onClick={() => setInterno(false)}>
            {t("chatElite.comun.responder")}
          </button>
          <button type="button" role="tab" aria-selected={interno} className={interno ? "activo" : ""} onClick={() => setInterno(true)}>
            <Lock size={12} /> {t("chatElite.comun.notaInterna")}
          </button>
        </div>
      )}
      <AdjuntosPendientes pendientes={adjuntos.pendientes} onQuitar={adjuntos.quitar} deshabilitado={enviando} antes={adjuntoFijo} />
      <div className="ce-compositor__fila">
        <BotonAdjuntar onSeleccion={adjuntos.anadir} deshabilitado={deshabilitado} numPendientes={adjuntos.pendientes.length} />
        <textarea
          ref={textareaRef}
          rows={filasMin}
          value={texto}
          maxLength={MAX_TEXTO}
          onChange={(e) => cambiarTexto(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={adjuntos.onPaste}
          placeholder={interno ? t("chatElite.comun.placeholderNota") : placeholder || t("chatElite.comun.placeholder")}
          disabled={deshabilitado}
          aria-label={placeholder || t("chatElite.comun.placeholder")}
        />
        {mostrarMicrofono ? (
          <LAEAIGrabador deshabilitado={deshabilitado || enviando} onAudio={enviarVoz} onError={errorMicrofono} t={t} />
        ) : (
          <button
            type="button"
            className="ce-compositor__enviar"
            onClick={enviar}
            disabled={!puedeEnviar}
            aria-label={t("chatElite.comun.enviar")}
          >
            <SendHorizontal size={18} />
          </button>
        )}
      </div>
      {texto.length > MAX_TEXTO * 0.9 && (
        <span className="ce-compositor__contador">{texto.length}/{MAX_TEXTO}</span>
      )}
    </div>
  );
};

export default Compositor;
